import React from 'react';
import { Plus, X } from 'lucide-react';

/**
 * Tenant-portal criteria editor (light theme). Purely controlled — the parent
 * form owns the criteria array; this component just renders + emits changes.
 *
 * Each criterion carries:
 *   - description (string)
 *   - weight (int)
 *   - applies_to (List[str]) — subset of {initial_sales, follow_up, non_sales}
 *     Empty list = applies to ALL call intents (safe legacy default).
 */

const APPLIES_TO_OPTIONS = [
  { key: 'initial_sales', label: 'Initial Sales', tone: 'sky' },
  { key: 'follow_up', label: 'Follow-Up', tone: 'amber' },
  { key: 'non_sales', label: 'Non-Sales', tone: 'slate' },
];

// Tailwind can't handle fully dynamic class names — enumerate them so
// PurgeCSS keeps them in the bundle.
const TONE_ACTIVE = {
  sky: 'bg-sky-100 text-sky-800 border-sky-300',
  amber: 'bg-amber-100 text-amber-800 border-amber-300',
  slate: 'bg-slate-200 text-slate-800 border-slate-400',
};
const TONE_INACTIVE =
  'bg-white text-slate-400 border-slate-200 hover:text-slate-700 hover:border-slate-300';

const AppliesToChips = ({ value, onChange, testIdPrefix }) => {
  const active = Array.isArray(value) ? value : [];
  const toggle = (key) => {
    const next = active.includes(key)
      ? active.filter((k) => k !== key)
      : [...active, key];
    onChange(next);
  };
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span
        className="text-slate-400 text-[10px] uppercase tracking-wide mr-1"
        title="Which call intents this criterion should be scored against. Leave all off = applies to all."
      >
        Applies to:
      </span>
      {APPLIES_TO_OPTIONS.map((opt) => {
        const isActive = active.includes(opt.key);
        return (
          <button
            type="button"
            key={opt.key}
            onClick={() => toggle(opt.key)}
            className={`text-[10px] font-medium px-2 py-0.5 rounded-full border transition-colors ${
              isActive ? TONE_ACTIVE[opt.tone] : TONE_INACTIVE
            }`}
            title={
              isActive
                ? `Currently scored on ${opt.label} calls`
                : `Not scored on ${opt.label} calls`
            }
            data-testid={`${testIdPrefix}-applies-${opt.key}`}
          >
            {opt.label}
          </button>
        );
      })}
      {active.length === 0 && (
        <span
          className="text-slate-400 text-[10px] italic ml-1"
          title="No filters — this criterion is graded on every call type."
        >
          all
        </span>
      )}
    </div>
  );
};

export const CriteriaEditor = ({ form, setForm }) => {
  const setCrit = (i, key, val) => {
    setForm((f) => {
      const next = [...f.criteria];
      next[i] = { ...next[i], [key]: val };
      return { ...f, criteria: next };
    });
  };
  const addCrit = () =>
    setForm((f) => ({
      ...f,
      criteria: [...f.criteria, { description: '', weight: 1, applies_to: [] }],
    }));
  const removeCrit = (i) =>
    setForm((f) => ({ ...f, criteria: f.criteria.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-2">
      {form.criteria.map((c, i) => (
        <div
          key={i}
          className="flex flex-col gap-1.5 bg-slate-50 border border-slate-200 rounded-lg p-2"
          data-testid={`kb-crit-row-${i}`}
        >
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-xs w-5 text-right">{i + 1}.</span>
            <input
              value={c.description}
              onChange={(e) => setCrit(i, 'description', e.target.value)}
              placeholder="Criteria description..."
              className="flex-1 bg-white border border-slate-200 rounded px-2 py-1 text-slate-900 text-sm focus:outline-none focus:border-primary/60"
              data-testid={`kb-crit-desc-${i}`}
            />
            <span className="text-slate-400 text-xs">Weight</span>
            <input
              type="number"
              min={1}
              value={c.weight}
              onChange={(e) => setCrit(i, 'weight', parseInt(e.target.value) || 1)}
              className="w-14 bg-white border border-slate-200 rounded px-1.5 py-1 text-slate-900 text-xs text-center focus:outline-none focus:border-primary/60"
              title="Any positive integer — higher = more important"
              data-testid={`kb-crit-weight-${i}`}
            />
            <button
              type="button"
              onClick={() => removeCrit(i)}
              className="text-slate-300 hover:text-red-500"
              data-testid={`kb-crit-remove-${i}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="pl-7">
            <AppliesToChips
              value={c.applies_to}
              onChange={(next) => setCrit(i, 'applies_to', next)}
              testIdPrefix={`kb-crit-${i}`}
            />
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addCrit}
        className="flex items-center gap-1.5 text-primary hover:text-primary/80 text-sm font-medium mt-1"
        data-testid="kb-crit-add"
      >
        <Plus className="w-4 h-4" /> Add criterion
      </button>
    </div>
  );
};
